import React, { useState, useEffect } from "react";
import { GET, POST } from '../services/api';
import { 
    Tag, Copy, Check, Clock, Loader2, 
    Ticket, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- Global Styles for Animations ---
const CustomStyles = () => (
  <style>{`
    .animate-fade-in-up { animation: fade-in-up 0.5s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

const ActiveCodes = () => {
    const navigate = useNavigate();
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchCodes = async () => {
        try {
            setLoading(true);
            const response = await GET('/codes/active');
            if (response && response.success) {
                setCodes(response.codes);
            } else {
                setError("Failed to load codes");
            }
        } catch (err) {
            console.error("API Error:", err);
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshCodes = async () => {
        try {
            setIsRefreshing(true);
            await POST('/codes/refresh');
            await fetchCodes();
            // Success toast
            // You can add toast logic here
            console.log('Codes refreshed successfully');
        } catch (err) {
            console.error('Refresh failed:', err);
            // Error toast
            setError('Failed to refresh codes');
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchCodes();
    }, []);

    const handleCopy = (code, id) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        // Main Container adjusted for Sidebar (ml-250px) and Navbar (pt-20/24)
        <div className="selection:bg-blue-100">
            <CustomStyles />

            <div>
                {/* --- Header Section (Like Image) --- */}
                <div className="mb-8">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">
                        Active Membership Codes
                    </h1>
                    <button 
                        onClick={handleRefreshCodes}
                        disabled={isRefreshing}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isRefreshing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Refreshing...
                            </>
                        ) : (
                            <>
                                Refresh Codes
                            </>
                        )}
                    </button>
                </div>
                    <p className="text-slate-500 mt-1">
                        Manage and distribute active coupon codes.
                    </p>
                </div>

                {/* --- Content Area --- */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64 text-[#1A87CF]">
                        <Loader2 className="w-10 h-10 animate-spin mb-3" />
                        <p className="text-slate-500 font-medium">Fetching active codes...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-100 rounded-3xl p-8 text-center max-w-lg mx-auto">
                        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                        <p className="text-red-700 font-medium">{error}</p>
                    </div>
                ) : (
                    // Responsive Grid: Adjusts automatically based on screen size
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                        {codes.length > 0 ? (
                            codes.map((item, index) => (
                                <div 
                                    key={item.id || index}
                                    className="group bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col"
                                >
                                    {/* Subtle Decoration */}
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full opacity-50 -mr-6 -mt-6 transition-transform group-hover:scale-110 pointer-events-none"></div>

                                    {/* Header: Label & Icon */}
                                    <div className="flex justify-between items-start mb-5 relative z-10">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${copiedId === (item.id || index) ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-[#1A87CF]'}`}>
                                            {copiedId === (item.id || index) ? <Check size={18} /> : <Tag size={18} />}
                                        </div>
                                        <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] uppercase font-bold px-2.5 py-1 rounded-full tracking-wide">
                                            Active
                                        </span>
                                    </div>

                                    {/* Code Display */}
                                    <div className="text-center mb-5 relative z-10">
                                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">Membership Code</div>
                                        <div 
                                            onClick={() => handleCopy(item.code, item.id || index)}
                                            className="cursor-pointer group/code bg-slate-50 rounded-lg py-2 border border-dashed border-slate-300 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                        >
                                            <h3 className="text-xl font-black text-slate-800 tracking-wider font-mono group-hover/code:text-[#1A87CF]">
                                                {item.code}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Footer Action */}
                                    <div className="mt-auto flex items-center justify-between relative z-10">
                                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase">
                                            <Clock size={12} />
                                            <span>Valid</span>
                                        </div>

                                        <button 
                                            onClick={() => handleCopy(item.code, item.id || index)}
                                            className={`
                                                flex items-center gap-1.5 text-xs font-bold transition-colors
                                                ${copiedId === (item.id || index) 
                                                    ? 'text-emerald-600' 
                                                    : 'text-slate-500 hover:text-[#1A87CF]'}
                                            `}
                                        >
                                            <span>{copiedId === (item.id || index) ? 'Copied' : 'Copy'}</span>
                                            {copiedId !== (item.id || index) && <Copy size={14} />}
                                        </button>
                                    </div>
                                </div> 
                            ))
                        ) : (
                            <div className="col-span-full flex flex-col items-center justify-center py-20 opacity-60">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                    <Ticket size={28} className="text-slate-400" />
                                </div>
                                <p className="text-slate-500 font-medium">No active codes found.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActiveCodes;